import React from 'react';
import { Bug } from 'lucide-react';
import { ErrorState } from './ui/ErrorState';

/**
 * Catches what a render throws, so one bad value cannot take the app down.
 *
 * It is the only class component in the project, and it has to be: `AppRouter`
 * builds its routes declaratively with `<BrowserRouter>` + `<Routes>`, so React
 * Router's `errorElement` / `useRouteError` are not available without rewriting
 * the router, and there is no hook equivalent of `componentDidCatch`.
 *
 * The failure it exists for is real and reachable: several thunks store the raw
 * `response.data` from axios, which for a DRF validation error is an **object**.
 * That object reaches `{error}` in JSX and React throws "Objects are not valid
 * as a React child" — a white screen with no way back other than the user
 * guessing they should reload.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-ninja-gray dark:bg-[#0f0f0f] flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl border border-border">
          <ErrorState
            icon={Bug}
            title="Algo salió mal"
            description="La página no pudo dibujarse. Recargar suele bastar; si vuelve a pasar, avísale al equipo."
            retryLabel="Recargar la página"
            // A full reload, not `setState`: whatever value broke the render is
            // still sitting in the store, so re-rendering the same tree would
            // throw again immediately.
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }
}
